import React, { memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { startOfTomorrow } from 'date-fns';

import { DatepickerRange, Modal, SpinnerOverlay, SubmissionButtons } from '@components/common';

import { useMutateCheckAcquireMultipleAsset } from '@hooks/assets/useMutateCheckAcquireMultipleAsset';
import { useMutateCheckAcquirePolygonAsset } from '@hooks/assets/useMutateCheckAcquirePolygonAsset';
import { useMutateSendAcquireAsset } from '@hooks/assets/useMutateSendAcquireAsset';
import { useMutateSendAcquireMultipleAssets } from '@hooks/assets/useMutateSendAcquireMultipleAssets';
import { useMutateSendAcquirePolygonAsset } from '@hooks/assets/useMutateSendAcquirePolygonAsset';
import { useRentRequestsDatesPicked } from '@hooks/useRentRequestsDatesPicked';

import { map } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { isEmpty } from '@utils/common';
import { getAssetFiltersDto } from '@utils/getAssetTypesDtoForFilter';
import { endOfDayInUTC, startOfDayInUTC } from '@utils/kpi-utils/date';

import { IAcquirerFormValues } from '@interfaces/rent/assetRent';

import { EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480 } from '@constants/modals';

import ConfirmActionModal from './ConfirmActionModal';
import { validationSchema } from './schema';

import styles from './AcquireAssetForm.module.scss';

interface IAcquireAssetFormProps {
	closeModal: () => void;
	setIsDirtyForm: React.Dispatch<React.SetStateAction<boolean>>;
}

const AcquireAssetForm = ({
	closeModal,
	setIsDirtyForm,
}: IAcquireAssetFormProps) => {
	const [assetDbIds, setAssetDbIds] = useState<string[]>([]);
	const [acquireAssetFrom, setAcquireAssetFrom] = useState<number | null>(null);
	const [acquireAssetTo, setAcquireAssetTo] = useState<number | null>(null);
	const [notPossibleToAddCount, setNotPossibleToAddCount] = useState(0);
	const [isConfirmActionOpen, setIsConfirmActionOpen] = useState(false);

	const openConfirmAction = () => setIsConfirmActionOpen(true);
	const closeConfirmAction = () => setIsConfirmActionOpen(false);

	const mapState = useAppSelector(map);

	const { activeBoroughs } = mapState;

	const {
		clearErrors,
		formState: { errors, isDirty },
		handleSubmit,
		setValue,
		watch,
	} = useForm<IAcquirerFormValues>({
		mode: EValidationMode.onSubmit,
		resolver: yupResolver(validationSchema),
	});

	const { activeAssetMarkers, activeCustomPolygons, assetIdsInsidePolygons } = useAppSelector(map);

	const isAcquireAssetsNotPolygon = isEmpty(activeCustomPolygons) && isEmpty(activeBoroughs);

	const {
		handleSendAcquirePolygonAsset,
		isSendingAcquirePolygonAsset,
	} = useMutateSendAcquirePolygonAsset([closeModal]);

	const {
		handleSendAcquireMultipleAsset,
		isSendingAcquireMultipleAsset,
	} = useMutateSendAcquireMultipleAssets([closeModal]);

	const { isSendingAcquireAsset } = useMutateSendAcquireAsset([closeModal]);

	const handleCheckAddToCartMultipleAssetCallback = useCallback((addToCartCount: number) => {
		if (isAcquireAssetsNotPolygon && addToCartCount === assetDbIds.length) {
			return handleSendAcquireMultipleAsset({
				assetDbIds: activeAssetMarkers,
				from: acquireAssetFrom,
				to: acquireAssetTo,
			});
		} else if (!isAcquireAssetsNotPolygon && addToCartCount === assetIdsInsidePolygons?.length) {
			return handleSendAcquirePolygonAsset({
				from: acquireAssetFrom,
				to: acquireAssetTo,
				...getAssetFiltersDto(mapState),
			});
		}

		isAcquireAssetsNotPolygon
			? setNotPossibleToAddCount(assetDbIds.length - addToCartCount)
			: setNotPossibleToAddCount(assetIdsInsidePolygons!.length - addToCartCount);

		openConfirmAction();
	}, [
		acquireAssetFrom,
		acquireAssetTo,
		activeAssetMarkers,
		assetDbIds.length,
		assetIdsInsidePolygons,
		handleSendAcquireMultipleAsset,
		handleSendAcquirePolygonAsset,
		isAcquireAssetsNotPolygon,
		mapState,
	]);

	const confirmAction = useCallback(() => {
		if (isAcquireAssetsNotPolygon) {
			return handleSendAcquireMultipleAsset({
				assetDbIds: activeAssetMarkers,
				from: acquireAssetFrom,
				to: acquireAssetTo,
			});
		}

		return handleSendAcquirePolygonAsset({
			from: acquireAssetFrom,
			to: acquireAssetTo,
			...getAssetFiltersDto(mapState),
		});
	}, [
		acquireAssetFrom,
		acquireAssetTo,
		activeAssetMarkers,
		handleSendAcquireMultipleAsset,
		handleSendAcquirePolygonAsset,
		isAcquireAssetsNotPolygon,
		mapState,
	]);

	const {
		handleCheckAddToCartMultipleAsset,
		isCheckingAddToCartMultipleAsset,
	} = useMutateCheckAcquireMultipleAsset(handleCheckAddToCartMultipleAssetCallback);

	const {
		handleCheckAddToCartPolygon,
		isCheckingAddToCartPolygon,
	} = useMutateCheckAcquirePolygonAsset(handleCheckAddToCartMultipleAssetCallback);

	const activeAssetMarker = activeAssetMarkers.length === 1 ? activeAssetMarkers[0] : '';

	const {
		rentRequestsDatesPicked,
	} = useRentRequestsDatesPicked(activeAssetMarker);

	const setFromDate = (date: number) => {
		if (date) clearErrors('rentalPeriodFrom');

		setValue('rentalPeriodFrom', date, { shouldDirty: true });
	};
	const setToDate = (date: number) => {
		if (date) clearErrors('rentalPeriodTo');

		setValue('rentalPeriodTo', date, { shouldDirty: true });
	};

	const onSubmit = (values: IAcquirerFormValues) => {
		const from = startOfDayInUTC(new Date(values.rentalPeriodFrom));
		const to = endOfDayInUTC(new Date(values.rentalPeriodTo));

		if (!isEmpty(activeAssetMarkers)) setAssetDbIds(activeAssetMarkers);

		setAcquireAssetFrom(from);
		setAcquireAssetTo(to);

		if (isAcquireAssetsNotPolygon) {
			return handleCheckAddToCartMultipleAsset({
				assetDbIds: activeAssetMarkers,
				from,
				to,
			});
		}

		if (!isAcquireAssetsNotPolygon) {
			return handleCheckAddToCartPolygon({
				from,
				to,
				...getAssetFiltersDto(mapState),
			});
		}

		handleCheckAddToCartMultipleAsset({
			assetDbIds: activeAssetMarkers,
			from,
			to,
		});
	};

	useEffect(() => setIsDirtyForm(isDirty), [isDirty, setIsDirtyForm]);

	const isSending = isSendingAcquireAsset || isSendingAcquireMultipleAsset || isSendingAcquirePolygonAsset;

	const excludeDateIntervals = activeAssetMarker ? rentRequestsDatesPicked : undefined;

	return (
		<>
			{(isCheckingAddToCartMultipleAsset || isCheckingAddToCartPolygon || isSending) && <SpinnerOverlay />}
			<div className={styles.datePickersContainer}>
				<DatepickerRange
					disableInput
					errorFromDate={errors?.rentalPeriodFrom?.message}
					errorToDate={errors?.rentalPeriodTo?.message}
					excludeDateIntervalsFrom={excludeDateIntervals}
					excludeDateIntervalsTo={excludeDateIntervals}
					groupLabel='Rental Period'
					handleChangeFromDate={setFromDate}
					handleChangeToDate={setToDate}
					isMobileDevice={false}
					isTodayButtonFrom={false}
					isTodayButtonTo={false}
					minDateFrom={startOfTomorrow()}
					minDateTo={startOfTomorrow()}
					requiredFromDate
					requiredToDate
					value={{
						from: watch('rentalPeriodFrom') as number,
						to: watch('rentalPeriodTo') as number,
					}}
				/>
			</div>
			<SubmissionButtons
				disabledPositive={!isEmpty(errors)}
				negativeHandler={closeModal}
				positiveHandler={handleSubmit(onSubmit)}
				positiveLabel='Add to Request list'
			/>
			<Modal
				isOpen={isConfirmActionOpen}
				onClose={closeConfirmAction}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<ConfirmActionModal
					closeModal={closeConfirmAction}
					confirmAction={confirmAction}
					isSending={isSending}
					notPossibleToAddCount={notPossibleToAddCount}
					totalAssets={(
						isAcquireAssetsNotPolygon
							? assetDbIds.length
							: assetIdsInsidePolygons?.length) as number
					}
				/>
			</Modal>
		</>
	);
};

export default memo(AcquireAssetForm);
